import React, { useState, useEffect, useCallback } from 'react';
import { X, CreditCard, ChevronRight, CheckCircle, Loader, AlertCircle, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { getTenantId } from '../../config/tenantConfig';
import { getValidAuthSession } from '../../utils/authStorage';
import './index.css';
import '../ProfileSectionPage/index.css';
import { settingsService } from '../../config/settingsService';

// API Configuration
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://restomate.live/api';
const X_API_KEY = process.env.REACT_APP_X_API_KEY;

const TRANSACTIONS_PAGE_LIMIT = 10;

export const MyTransactionsModal = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const [transactions, setTransactions] = useState([]);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [lastPage, setLastPage] = useState(1);

  const [activeFilters, setActiveFilters] = useState({
    search: '',
    fromDate: '',
    toDate: ''
  });
  const [tempFilters, setTempFilters] = useState({
    search: '',
    fromDate: '',
    toDate: ''
  });
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setPage(1);
      setSelectedTransaction(null);
      fetchTransactions(1);
    }
  }, [isOpen, activeFilters, perPage]);

  const fetchTransactions = useCallback(async (currentPage = 1) => {
    setLoading(true);
    setError(null);

    try {
      const token = getValidAuthSession()?.token;

      if (!token) {
        setError('Please login to view your transactions');
        setLoading(false);
        return;
      }

      const params = {
        paginate: 1,
        page: currentPage,
        per_page: perPage,
        order_column: 'id',
        order_by: 'desc'
      };

      if (activeFilters.search) {
        const cleanSearch = activeFilters.search.replace(/#/g, '').trim();
        params.search = activeFilters.search;
        params.transaction_no = activeFilters.search;
        params.transaction_id = activeFilters.search;
        params.serial_no = cleanSearch;
        params.id = cleanSearch;
        params.transaction_no_search = activeFilters.search;
        params.ref_no = cleanSearch;
        params.query = cleanSearch;
      }
      if (activeFilters.fromDate) params.from_date = new Date(activeFilters.fromDate).toISOString();
      if (activeFilters.toDate) params.to_date = new Date(activeFilters.toDate).toISOString();

      const response = await axios.get(`${API_BASE_URL}/web/transactions`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Api-Key': X_API_KEY,
          'X-Tenant': getTenantId(),
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        params
      });

      if (response.data && response.data.data) {
        const formattedTransactions = response.data.data.map(txn => ({
          id: txn.transaction_no || txn.id || 'N/A', // Show transaction_no as the primary ID
          dbId: txn.id,
          transactionNo: txn.transaction_no || 'N/A',
          orderId: txn.order_serial_no || txn.order_id || 'N/A',
          date: txn.date || 'N/A',
          amount: txn.amount ? settingsService.formatPrice(txn.amount) : settingsService.formatPrice(0),
          method: getPaymentMethodText(txn.payment_method),
          type: txn.type || 'N/A',
          sign: txn.sign || '+',
          status: 'Success',
          gateway: detectGateway(txn.transaction_no),
          rawData: txn
        }));

        setTransactions(formattedTransactions);
        setTotalCount(response.data.total || response.data.meta?.total || 0);
        setLastPage(response.data.last_page || response.data.meta?.last_page || 1);
        setPage(currentPage);
      }
    } catch (err) {
      console.error('Error fetching transactions:', err);
      setError(err.response?.data?.message || 'Failed to load transactions');
    } finally {
      setLoading(false);
    }
  }, [activeFilters, perPage]);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= lastPage) {
      fetchTransactions(newPage);
    }
  };

  const handleTempFilterChange = (field, value) => {
    setTempFilters(prev => ({ ...prev, [field]: value }));
  };

  const applyFilters = () => {
    setActiveFilters({ ...tempFilters });
    setPage(1);
  };

  const clearFilters = () => {
    const emptyFilters = {
      search: '',
      fromDate: '',
      toDate: ''
    };
    setTempFilters(emptyFilters);
    setActiveFilters(emptyFilters);
    setPage(1);
  };

  // ✅ FIXED: Match the actual API response format
  const getPaymentMethodText = (method) => {
    if (!method) return 'Unknown';

    // Handle string values from API
    const methodLower = String(method).toLowerCase();

    if (methodLower.includes('cash')) return 'Cash';
    if (methodLower.includes('card')) return 'Card';
    if (methodLower.includes('upi')) return 'UPI';
    if (methodLower.includes('online')) return 'Online';

    // Handle numeric values
    const methodMap = {
      1: 'Cash',
      2: 'Card',
      3: 'UPI',
      5: 'Online'
    };

    return methodMap[method] || String(method);
  };

  // ✅ NEW: Detect payment gateway from transaction number
  const detectGateway = (transactionNo) => {
    if (!transactionNo) return 'Unknown';

    const txnStr = String(transactionNo).toLowerCase();

    if (txnStr.includes('cash')) return 'Cash';
    if (txnStr.startsWith('pay_')) return 'Razorpay';
    if (txnStr.startsWith('cs_') || txnStr.startsWith('pi_')) return 'Stripe';
    if (txnStr.length > 10 && !txnStr.includes('_')) return 'Cashfree';

    return 'Card Payment';
  };

  const handleClose = () => {
    setSelectedTransaction(null);
    onClose();
  };

  if (!isOpen) return null;

  const getStatusColor = (status) => {
    const statusLower = String(status).toLowerCase();
    if (statusLower.includes('success') || statusLower.includes('credit')) return '#4CAF50';
    if (statusLower.includes('fail') || statusLower.includes('debit')) return '#dc3545';
    return '#FF9800';
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="profile-section-header">
        <div className="profile-section-header-inner">
          <button 
            onClick={() => navigate(-1)} 
            style={{ background: 'none', border: 'none', padding: 0, marginRight: '15px', display: 'flex', alignItems: 'center', cursor: 'pointer', color: 'var(--color-primary, #ff6b35)' }}
            title="Go Back"
          >
            <ArrowLeft size={28} />
          </button>
          <CreditCard size={28} />
          <h2>My Transactions</h2>
        </div>
      </div>

      <div className="profile-section-content">
        {loading ? (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '400px',
            gap: '20px'
          }}>
            <Loader size={40} style={{ animation: 'spin 1s linear infinite' }} />
            <p style={{ color: '#666', fontSize: '16px' }}>Loading transactions...</p>
            <style>{`
              @keyframes spin {
                from { transform: rotate(0deg); }
                to { transform: rotate(360deg); }
              }
            `}</style>
          </div>
        ) : error ? (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '400px',
            gap: '20px'
          }}>
            <AlertCircle size={48} color="#dc3545" />
            <p style={{ color: '#dc3545', fontSize: '16px', textAlign: 'center' }}>{error}</p>
            <button
              onClick={() => fetchTransactions(1)}
              style={{
                padding: '10px 24px',
                backgroundColor: '#ff6b35',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '500',
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
              onMouseOver={(e) => e.target.style.backgroundColor = '#e55a2b'}
              onMouseOut={(e) => e.target.style.backgroundColor = '#ff6b35'}
            >
              Try Again
            </button>
          </div>
        ) : !selectedTransaction ? (
          <>
            {/* Filters Section - Shared Between Results and Empty States */}
            <div className="orders-filters-bar">
              <div className="filters-main-row">
                <div className="filter-item">
                  <select
                    value={perPage}
                    onChange={(e) => {
                      setPerPage(parseInt(e.target.value));
                      setPage(1);
                    }}
                    className="filter-select per-page-select"
                  >
                    <option value="10">10</option>
                    <option value="20">20</option>
                    <option value="50">50</option>
                  </select>
                </div>

                <button
                  className={`filter-toggle-btn ${isFilterOpen ? 'active' : ''}`}
                  onClick={() => setIsFilterOpen(!isFilterOpen)}
                >
                  <AlertCircle size={16} />
                  <span>Filters</span>
                  <ChevronRight size={14} className={isFilterOpen ? 'rotate-down' : ''} />
                </button>
              </div>

              {isFilterOpen && (
                <div className="filters-expanded-panel">
                  <div className="filters-grid">
                    <div className="filter-field">
                      <label>Transaction ID</label>
                      <input
                        type="text"
                        placeholder="Enter Transaction ID"
                        value={tempFilters.search}
                        onChange={(e) => handleTempFilterChange('search', e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && applyFilters()}
                        className="filter-input-search"
                      />
                    </div>

                    <div className="filter-field">
                      <label>From Date</label>
                      <input
                        type="date"
                        value={tempFilters.fromDate}
                        onChange={(e) => handleTempFilterChange('fromDate', e.target.value)}
                      />
                    </div>

                    <div className="filter-field">
                      <label>To Date</label>
                      <input
                        type="date"
                        value={tempFilters.toDate}
                        onChange={(e) => handleTempFilterChange('toDate', e.target.value)}
                      />
                    </div>
                  </div>

                  <div className="filters-actions">
                    <button className="clear-filters-btn" onClick={clearFilters}>Clear All</button>
                    <button className="apply-filters-btn" onClick={applyFilters}>Search</button>
                  </div>
                </div>
              )}
            </div>

            {transactions.length === 0 ? (
              <div className="orders-state-container empty" style={{ minHeight: '300px' }}>
                <CreditCard size={64} className="empty-icon" style={{ opacity: 0.3 }} />
                <h3 className="empty-title">
                  {Object.values(activeFilters).some(v => v !== '') ? 'No Transaction Found' : 'No Transactions Yet'}
                </h3>
                <p className="empty-text">
                  {Object.values(activeFilters).some(v => v !== '') ? 'Try adjusting your filters' : 'Your transaction history will appear here'}
                </p>
                {Object.values(activeFilters).some(v => v !== '') && (
                  <button onClick={clearFilters} className="clear-filters-btn secondary" style={{ marginTop: '16px' }}>
                    Clear All Filters
                  </button>
                )}
              </div>
            ) : (
              <>
                {/* Desktop Table View */}
                <div className="transactions-table-container">
                  <table className="transactions-table">
                    <thead>
                      <tr>
                        <th>Transaction ID</th>
                        <th>Date & Time</th>
                        <th>Order ID</th>
                        <th>Method</th>
                        <th>Amount</th>
                        <th>Status</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {transactions.map(txn => (
                        <tr key={txn.id}>
                          <td>
                            <div className="transaction-id-cell">
                              <CreditCard size={16} />
                              <strong>{txn.id}</strong>
                            </div>
                          </td>
                          <td>{txn.date}</td>
                          <td>{txn.orderId}</td>
                          <td>{txn.method}</td>
                          <td className="total-cell">{txn.amount}</td>
                          <td>
                            <div className="transaction-status" style={{
                              backgroundColor: `${getStatusColor(txn.status)}20`,
                              color: getStatusColor(txn.status)
                            }}>
                              <CheckCircle size={16} />
                              <span>{txn.status}</span>
                            </div>
                          </td>
                          <td>
                            <button className="view-details-btn" onClick={() => setSelectedTransaction(txn)}>
                              View Details
                              <ChevronRight size={16} />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Mobile Cards View */}
                <div className="transactions-list">
                  {transactions.map(txn => (
                    <div key={txn.id} className="transaction-card" onClick={() => setSelectedTransaction(txn)}>
                      <div className="transaction-card-header">
                        <div className="transaction-id-section">
                          <CreditCard size={18} />
                          <span>{txn.id}</span>
                        </div>
                        <div className="transaction-status" style={{
                          backgroundColor: `${getStatusColor(txn.status)}20`,
                          color: getStatusColor(txn.status)
                        }}>
                          <CheckCircle size={16} />
                          <span>{txn.status}</span>
                        </div>
                      </div>
                      <div className="transaction-card-body">
                        <div className="transaction-info-row">
                          <span className="transaction-label">Date & Time</span>
                          <span className="transaction-value">{txn.date}</span>
                        </div>
                        <div className="transaction-info-row">
                          <span className="transaction-label">Order ID</span>
                          <span className="transaction-value">{txn.orderId}</span>
                        </div>
                        <div className="transaction-info-row">
                          <span className="transaction-label">Method</span>
                          <span className="transaction-value">{txn.method}</span>
                        </div>
                        <div className="transaction-info-row">
                          <span className="transaction-label">Total Amount</span>
                          <span className="transaction-value-bold">{txn.amount}</span>
                        </div>
                      </div>
                      <div className="transaction-card-footer">
                        <span>View Details</span>
                        <ChevronRight size={16} />
                      </div>
                    </div>
                  ))}
                </div>

                {/* Pagination Component */}
                <div className="custom-pagination">
                  <button
                    className="pagination-arrow"
                    disabled={page === 1}
                    onClick={() => handlePageChange(page - 1)}
                  >
                    <ChevronRight size={18} style={{ transform: 'rotate(180deg)' }} />
                  </button>

                  <div className="pagination-pages">
                    {[...Array(lastPage)].map((_, i) => {
                      const p = i + 1;
                      if (
                        p === 1 ||
                        p === lastPage ||
                        (p >= page - 1 && p <= page + 1)
                      ) {
                        return (
                          <button
                            key={p}
                            className={`page-number ${page === p ? 'active' : ''}`}
                            onClick={() => handlePageChange(p)}
                          >
                            {p}
                          </button>
                        );
                      } else if (p === page - 2 || p === page + 2) {
                        return <span key={p} className="pagination-ellipsis">...</span>;
                      }
                      return null;
                    })}
                  </div>

                  <button
                    className="pagination-arrow"
                    disabled={page === lastPage}
                    onClick={() => handlePageChange(page + 1)}
                  >
                    <ChevronRight size={18} />
                  </button>
                </div>
              </>
            )}
          </>
        ) : (
          <div className="transaction-details">
            <button className="back-button" onClick={() => setSelectedTransaction(null)}>
              <ChevronRight size={18} className="back-button-icon" />
              <span>Back to Transactions</span>
            </button>

            <div className="transaction-details-card">
              <div className="transaction-details-header">
                <h3>Transaction {selectedTransaction.id}</h3>
                <div className="transaction-status" style={{
                  backgroundColor: `${getStatusColor(selectedTransaction.status)}20`,
                  color: getStatusColor(selectedTransaction.status)
                }}>
                  <CheckCircle size={20} />
                  <span>{selectedTransaction.status}</span>
                </div>
              </div>

              <div className="transaction-details-section">
                <h4>Transaction Information</h4>

                <div className="detail-row">
                  <span className="detail-label">Transaction ID:</span>
                  <span className="detail-value">{selectedTransaction.id}</span>
                </div>

                {/* <div className="detail-row">
                  <span className="detail-label">Transaction Number:</span>
                  <span className="detail-value">{selectedTransaction.transactionNo}</span>
                </div> */}

                <div className="detail-row">
                  <span className="detail-label">Date & Time:</span>
                  <span className="detail-value">{selectedTransaction.date}</span>
                </div>

                <div className="detail-row">
                  <span className="detail-label">Order ID:</span>
                  <span className="detail-value">{selectedTransaction.orderId}</span>
                </div>

                <div className="detail-row">
                  <span className="detail-label">Payment Method:</span>
                  <span className="detail-value">{selectedTransaction.method}</span>
                </div>

                <div className="detail-row">
                  <span className="detail-label">Payment Gateway:</span>
                  <span className="detail-value">{selectedTransaction.gateway}</span>
                </div>

                <div className="detail-row">
                  <span className="detail-label">Transaction Type:</span>
                  <span className="detail-value">{selectedTransaction.type}</span>
                </div>
              </div>

              <div className="transaction-total-section">
                <span>Total Amount</span>
                <strong>{selectedTransaction.amount}</strong>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};